// Pago a instructores por servicios profesionales (pedido del cliente
// 2026-09-28, con el "Reporte de Pago" de ejemplo: Cursos Renovación
// 33 × $20 = $660, retención 10% = $66, neto $594).
//
// Modelo:
//   - A cada instructor se le paga POR ALUMNO QUE ASISTIÓ (status
//     'completed') a una sesión de curso en la que estuvo marcado. Quién dio
//     cada sesión sale de `course_session_meta` (se graba al imprimir la hoja
//     de asistencia en Cursos, o desde la página Pago a instructores).
//   - La tarifa por alumno se configura por curso en `settings/instructorPay`.
//     En el curso privado depende del idioma y de si es licencia nueva o
//     renovación (por eso se piden al reservar).
//   - Retención por servicios profesionales: 10 %, pero NO sobre los primeros
//     $500 que el instructor cobra en el año natural (aviso del cliente
//     2026-09-28). Se acumula lo pagado desde el 1 de enero hasta el día
//     antes del periodo y la retención solo aplica a lo que pase de $500.
import { isPrivateCourse, courseLanguageLabel, licenseTypeLabel } from './privateCourse';

export const DEFAULT_EXEMPT_CENTS = 50000; // primeros $500 del año sin retención
export const DEFAULT_RETENTION_PCT = 10;

export const INSTRUCTOR_PAY_SETTINGS_DOC = 'instructorPay';

// Combinaciones del curso privado: idioma × tipo de licencia
export const PRIVATE_RATE_KEYS = [
  { key: 'es_new', label: 'Español · Licencia nueva' },
  { key: 'es_renewal', label: 'Español · Renovación' },
  { key: 'en_new', label: 'Inglés · Licencia nueva' },
  { key: 'en_renewal', label: 'Inglés · Renovación' },
];

export const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

export const toCents = (dollars) => Math.round((parseFloat(String(dollars).replace(/[^0-9.]/g, '')) || 0) * 100);

export function normalizeSettings(raw) {
  const s = raw || {};
  return {
    rates: s.rates || {},
    privateRates: s.privateRates || {},
    exemptCents: s.exemptCents != null ? Number(s.exemptCents) : DEFAULT_EXEMPT_CENTS,
    retentionPct: s.retentionPct != null ? Number(s.retentionPct) : DEFAULT_RETENTION_PCT,
  };
}

// Mismo id que usa Cursos al imprimir la hoja: categoría__fecha__hora
export const sessionKey = (categoryId, dateISO, startTime) => `${categoryId}__${dateISO}__${startTime}`;

export const isCourseReservation = (r) => r.categoryType === 'course' || r.categoryType === 'class';

// Tarifa por alumno para una reservación (en centavos) y la etiqueta de la
// línea del reporte. En el curso privado la tarifa depende de idioma + tipo.
export function rateFor(reservation, settings, category) {
  const s = normalizeSettings(settings);
  const cat = category || { name: reservation.categoryName, type: reservation.categoryType };
  const name = (category && category.name) || reservation.categoryName || 'Curso';
  if (isPrivateCourse(cat)) {
    const key = `${reservation.courseLanguage || ''}_${reservation.licenseType || ''}`;
    const byKey = (s.privateRates[reservation.categoryId] || {})[key];
    const detail = [courseLanguageLabel(reservation.courseLanguage), licenseTypeLabel(reservation.licenseType)].filter(Boolean).join(' · ');
    const rateCents = byKey != null ? Number(byKey) : (s.rates[reservation.categoryId] != null ? Number(s.rates[reservation.categoryId]) : null);
    return { rateCents, label: detail ? `${name} · ${detail}` : name, missing: rateCents == null };
  }
  const rateCents = s.rates[reservation.categoryId] != null ? Number(s.rates[reservation.categoryId]) : null;
  return { rateCents, label: name, missing: rateCents == null };
}

// Retención: pct % solo sobre lo que pase del monto exento en el año.
// ytdBeforeCents = bruto cobrado desde el 1 de enero hasta antes del periodo.
export function computeRetention(grossCents, ytdBeforeCents, settings) {
  const s = normalizeSettings(settings);
  const exemptLeft = Math.max(0, s.exemptCents - Math.max(0, ytdBeforeCents));
  const exemptUsed = Math.min(grossCents, exemptLeft);
  const taxableCents = Math.max(0, grossCents - exemptUsed);
  const retentionCents = Math.round((taxableCents * s.retentionPct) / 100);
  return {
    exemptLeftCents: exemptLeft,
    exemptUsedCents: exemptUsed,
    taxableCents,
    retentionCents,
    netCents: grossCents - retentionCents,
  };
}

// Arma las sesiones de curso a partir de las reservaciones y los metadatos.
//   reservations: docs de `reservations` (cursos, cualquier status)
//   metas: docs de `course_session_meta`
// Devuelve un array de sesiones { key, categoryId, categoryName, date,
// startTime, endTime, instructorIds, instructorNames, attended[], total }.
export function buildSessions(reservations, metas) {
  const metaByKey = new Map((metas || []).map((m) => [m.id || sessionKey(m.categoryId, m.date, m.startTime), m]));
  const sessions = new Map();
  (reservations || []).filter(isCourseReservation).forEach((r) => {
    const key = sessionKey(r.categoryId, r.date, r.startTime);
    if (!sessions.has(key)) {
      const meta = metaByKey.get(key);
      sessions.set(key, {
        key,
        categoryId: r.categoryId,
        categoryName: r.categoryName || (meta && meta.categoryName) || 'Curso',
        date: r.date,
        startTime: r.startTime,
        endTime: r.endTime || '',
        instructorIds: (meta && meta.instructorIds) || [],
        instructorNames: (meta && meta.instructorNames) || [],
        attended: [],
        total: 0,
      });
    }
    const s = sessions.get(key);
    if (r.status === 'completed') s.attended.push(r);
    if (r.status !== 'cancelled') s.total += 1;
  });
  return [...sessions.values()].sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime));
}

// Reporte de un instructor para el periodo [startDate, endDate] (ISO).
// sessions: TODAS las sesiones desde el 1 de enero hasta endDate (para el
// acumulado del año). Solo cuentan los alumnos con status 'completed'.
export function instructorReport(instructor, sessions, { startDate, endDate, settings, categories = [] }) {
  const catById = new Map(categories.map((c) => [c.id, c]));
  const mine = sessions.filter((s) => (s.instructorIds || []).includes(instructor.id));
  const grossOf = (list) => list.reduce((sum, s) => sum + s.attended.reduce((a, r) => {
    const { rateCents } = rateFor(r, settings, catById.get(r.categoryId));
    return a + (rateCents || 0);
  }, 0), 0);

  const periodSessions = mine.filter((s) => s.date >= startDate && s.date <= endDate);
  const beforeSessions = mine.filter((s) => s.date < startDate);

  // Líneas por curso (y en el privado, por idioma/tipo): cantidad × tarifa
  const lines = new Map();
  let missingRate = 0;
  periodSessions.forEach((s) => {
    s.attended.forEach((r) => {
      const { rateCents, label, missing } = rateFor(r, settings, catById.get(r.categoryId));
      if (missing) missingRate += 1;
      const key = `${label}|${rateCents || 0}`;
      const line = lines.get(key) || { label, rateCents: rateCents || 0, count: 0, subtotalCents: 0 };
      line.count += 1;
      line.subtotalCents += rateCents || 0;
      lines.set(key, line);
    });
  });

  const grossCents = grossOf(periodSessions);
  const ytdBeforeCents = grossOf(beforeSessions);
  const ret = computeRetention(grossCents, ytdBeforeCents, settings);
  return {
    instructor,
    startDate,
    endDate,
    sessions: periodSessions,
    students: periodSessions.reduce((a, s) => a + s.attended.length, 0),
    lines: [...lines.values()].sort((a, b) => a.label.localeCompare(b.label, 'es') || a.rateCents - b.rateCents),
    missingRate,
    grossCents,
    ytdBeforeCents,
    ...ret,
    ytdAfterCents: ytdBeforeCents + grossCents,
  };
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

// "18 al 24 de septiembre de 2026" / "1 de septiembre al 3 de octubre de 2026"
export function periodLabel(startDate, endDate) {
  if (!startDate || !endDate) return '';
  const [y1, m1, d1] = startDate.split('-').map(Number);
  const [y2, m2, d2] = endDate.split('-').map(Number);
  if (startDate === endDate) return `${d1} de ${MESES[m1 - 1]} de ${y1}`;
  if (y1 === y2 && m1 === m2) return `${d1} al ${d2} de ${MESES[m1 - 1]} de ${y1}`;
  if (y1 === y2) return `${d1} de ${MESES[m1 - 1]} al ${d2} de ${MESES[m2 - 1]} de ${y1}`;
  return `${d1} de ${MESES[m1 - 1]} de ${y1} al ${d2} de ${MESES[m2 - 1]} de ${y2}`;
}

// Fecha de hoy en español, para la cabecera del reporte (pedido 2026-10-09)
export function todayLabel(d = new Date()) {
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
}

const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// HTML de un reporte (mismo formato que el PDF de ejemplo del cliente).
export function instructorReportHtml(report, settings) {
  const s = normalizeSettings(settings);
  const lineRows = report.lines.length === 0
    ? '<tr><td colspan="4" class="muted">Sin alumnos atendidos en el periodo.</td></tr>'
    : report.lines.map((l) => `
      <tr>
        <td>${esc(l.label)}</td>
        <td class="num">${l.count}</td>
        <td class="num">${money(l.rateCents)}</td>
        <td class="num">${money(l.subtotalCents)}</td>
      </tr>`).join('');
  const exemptNote = report.exemptUsedCents > 0
    ? `<tr><td>Exento — primeros ${money(s.exemptCents)} del año (acumulado antes del periodo: ${money(report.ytdBeforeCents)})</td><td class="num">− ${money(report.exemptUsedCents)}</td></tr>
       <tr><td>Base sujeta a retención</td><td class="num">${money(report.taxableCents)}</td></tr>`
    : `<tr><td class="muted">Acumulado del año antes del periodo: ${money(report.ytdBeforeCents)} (los primeros ${money(s.exemptCents)} ya están exentos)</td><td></td></tr>`;
  return `
    <section class="report">
      <h1>REPORTE DE PAGO DE SERVICIOS PROFESIONALES</h1>
      <div class="meta">
        <div><b>Instructor:</b> ${esc(report.instructor.name)}</div>
        <div><b>Periodo de Pago:</b> ${esc(periodLabel(report.startDate, report.endDate))}</div>
        <div><b>Fecha de emisión:</b> ${esc(todayLabel())}</div>
      </div>
      <h2>Desglose de Cursos Impartidos</h2>
      <table>
        <thead><tr><th>Descripción / Tipo de Curso</th><th class="num">Cantidad</th><th class="num">Tarifa Unitario</th><th class="num">Subtotal</th></tr></thead>
        <tbody>${lineRows}</tbody>
      </table>
      <h2>Resumen Financiero</h2>
      <table>
        <thead><tr><th>Concepto</th><th class="num">Monto</th></tr></thead>
        <tbody>
          <tr><td>Total Bruto</td><td class="num">${money(report.grossCents)}</td></tr>
          ${exemptNote}
          <tr><td>Retención por Servicios Profesionales (−${s.retentionPct}%)</td><td class="num">−${money(report.retentionCents)}</td></tr>
          <tr class="total"><td>TOTAL NETO A PAGAR</td><td class="num">${money(report.netCents)}</td></tr>
        </tbody>
      </table>
      <div class="foot">Sesiones: ${report.sessions.length} · Alumnos: ${report.students} · Acumulado del año después de este pago: ${money(report.ytdAfterCents)}</div>
    </section>`;
}

export function instructorReportsDocument(reports, settings) {
  const body = reports.map((r) => instructorReportHtml(r, settings)).join('<div class="page-break"></div>');
  return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8" />
    <title>Reporte de Pago — Instructores</title>
    <style>
      body { font-family: Arial, Helvetica, sans-serif; margin: 32px; color: #222; }
      h1 { font-size: 20px; margin: 0 0 12px; letter-spacing: .5px; }
      h2 { font-size: 14px; margin: 20px 0 6px; }
      .meta { display: flex; gap: 32px; flex-wrap: wrap; font-size: 13px; margin-bottom: 8px; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; }
      th, td { border: 1px solid #999; padding: 6px 8px; text-align: left; vertical-align: top; }
      th { background: #f0f0f0; }
      td.num, th.num { text-align: right; white-space: nowrap; }
      tr.total td { font-weight: bold; background: #f7f7f7; }
      .muted { color: #666; }
      .foot { color: #666; font-size: 11px; margin-top: 14px; }
      .page-break { page-break-after: always; }
      @media print { body { margin: 12px; } }
    </style></head><body>${body}
    <script>window.onload = () => window.print();</script>
  </body></html>`;
}

// PDF real para enviar por email al contable (video de Tuto 2026-10-09: antes
// había que imprimir y escanear). Un instructor por página, mismo contenido
// que el reporte impreso. Nombre: Pago-instructores-<desde>_<hasta>.pdf
export async function downloadInstructorReportsPdf(reports, settings) {
  const [{ jsPDF }, autoTableMod] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  // jspdf-autotable exporta la función como default y además registra pdf.autoTable
  const fn = [autoTableMod.default, autoTableMod.default && autoTableMod.default.default, autoTableMod.autoTable, autoTableMod].find((x) => typeof x === 'function');
  const s = normalizeSettings(settings);
  const pdf = new jsPDF({ unit: 'pt', format: 'letter' }); // eslint-disable-line new-cap
  const autoTable = (doc, opts) => (typeof doc.autoTable === 'function' ? doc.autoTable(opts) : fn(doc, opts));
  const W = pdf.internal.pageSize.getWidth();
  reports.forEach((report, idx) => {
    if (idx > 0) pdf.addPage();
    let y = 54;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
    pdf.text('B&B TARGET CENTER, INC.', 40, y); y += 18;
    pdf.setFontSize(12);
    pdf.text('REPORTE DE PAGO DE SERVICIOS PROFESIONALES', 40, y); y += 22;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10.5);
    pdf.text(`Instructor: ${report.instructor.name}`, 40, y); y += 15;
    pdf.text(`Periodo de pago: ${periodLabel(report.startDate, report.endDate)}`, 40, y); y += 15;
    pdf.text(`Fecha de emisión: ${todayLabel()}`, 40, y); y += 20;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
    pdf.text('Desglose de cursos impartidos', 40, y); y += 6;
    autoTable(pdf, {
      startY: y,
      margin: { left: 40, right: 40 },
      head: [['Descripción / Tipo de curso', 'Cantidad', 'Tarifa unitario', 'Subtotal']],
      body: report.lines.length === 0
        ? [['Sin alumnos atendidos en el periodo.', '', '', '']]
        : report.lines.map((l) => [l.label, String(l.count), money(l.rateCents), money(l.subtotalCents)]),
      styles: { fontSize: 9.5, cellPadding: 5, lineColor: [150, 150, 150], lineWidth: 0.5 },
      headStyles: { fillColor: [240, 240, 240], textColor: 30, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
      theme: 'grid',
    });
    y = pdf.lastAutoTable.finalY + 22;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
    pdf.text('Resumen financiero', 40, y); y += 6;
    const summary = [['Total bruto', money(report.grossCents)]];
    if (report.exemptUsedCents > 0) {
      summary.push([`Exento — primeros ${money(s.exemptCents)} del año (acumulado antes del periodo: ${money(report.ytdBeforeCents)})`, `- ${money(report.exemptUsedCents)}`]);
      summary.push(['Base sujeta a retención', money(report.taxableCents)]);
    } else {
      summary.push([`Acumulado del año antes del periodo: ${money(report.ytdBeforeCents)} (los primeros ${money(s.exemptCents)} ya están exentos)`, '']);
    }
    summary.push([`Retención por servicios profesionales (-${s.retentionPct}%)`, `- ${money(report.retentionCents)}`]);
    summary.push(['TOTAL NETO A PAGAR', money(report.netCents)]);
    autoTable(pdf, {
      startY: y,
      margin: { left: 40, right: 40 },
      head: [['Concepto', 'Monto']],
      body: summary,
      styles: { fontSize: 9.5, cellPadding: 5, lineColor: [150, 150, 150], lineWidth: 0.5 },
      headStyles: { fillColor: [240, 240, 240], textColor: 30, fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: W - 80 - 110 }, 1: { halign: 'right', cellWidth: 110 } },
      didParseCell: (data) => { if (data.section === 'body' && data.row.index === summary.length - 1) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.fillColor = [247, 247, 247]; } },
      theme: 'grid',
    });
    y = pdf.lastAutoTable.finalY + 16;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); pdf.setTextColor(110);
    pdf.text(`Sesiones: ${report.sessions.length} · Alumnos: ${report.students} · Acumulado del año después de este pago: ${money(report.ytdAfterCents)}`, 40, y);
    y += 40;
    pdf.setTextColor(30); pdf.setFontSize(9.5);
    pdf.line(40, y, 250, y); pdf.line(W - 250, y, W - 40, y);
    pdf.text('Firma del instructor', 40, y + 12); pdf.text('B&B Target Center, Inc.', W - 250, y + 12);
  });
  const name = reports.length === 1
    ? `Pago-${reports[0].instructor.name.replace(/[^\w]+/g, '-')}-${reports[0].startDate}_${reports[0].endDate}.pdf`
    : `Pago-instructores-${reports[0].startDate}_${reports[0].endDate}.pdf`;
  pdf.save(name);
}

export function openInstructorReports(reports, settings) {
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(instructorReportsDocument(reports, settings));
  w.document.close();
}
